import React, { useEffect, useState } from "react";
import { Heart, LucideIcon, Share2, Trash2 } from "lucide-react";
import BaseApi from "../services/base-api";
import { Favorite } from "../types/Favorite";
import ProductsCarousel from "../components/Product/carasoul";
import "../styles/favourites.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../components/Modals/Confirm";
import { useToast } from "../context/ToastContext";
import LoadingComponent from "../components/LoadingComponent";
import Pagination from "../components/Pagination";
import { usePagination } from "../hooks/usePagination";

interface ConfirmConfig {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;

  // NEW
  showInput?: boolean;
  inputPlaceholder?: string;
  inputLabel?: string;
  requiredInput?: boolean;

  icon?: LucideIcon;
  iconColor?: string;
  iconBg?: string;

  onConfirm: () => void;
}

const FavoritesManagement: React.FC = () => {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingAll, setRemovingAll] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { success, err } = useToast();

  const [favCount, setFavCount] = useState(0);

  const [confirmConfig, setConfirmConfig] = useState<ConfirmConfig>({
    isOpen: false,
    title: "",
    message: "",

    icon: undefined,
    iconColor: "red",
    iconBg: "transparent",

    showInput: false,
    inputPlaceholder: "",
    inputLabel: "",
    requiredInput: false,

    confirmText: t("Confirm"),
    cancelText: t("Cancel"),
    onConfirm: () => {},
  });

  const {
    currentPage,
    pageSize,
    totalPages,
    totalItems,
    setPage,
    setTotalItems,
  } = usePagination({ initialPageSize: 10 });

  // Modify fetchFavorites:
  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const res = await BaseApi.get(
        `/favorites?page=${currentPage}&limit=${pageSize}`,
      );
      if (res?.data) {
        setFavorites(res.data.favorites || res.data);
        setTotalItems(res.data.total || res.data.length);
      }
    } catch (error) {
      console.error("Failed to fetch favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  // Add useEffect:
  useEffect(() => {
    fetchFavorites();
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchFavorites();
    fetchFavCount();
    window.addEventListener(
      "wishlistUpdated",
      handleWishlistUpdate as EventListener,
    );
    return () => {
      window.removeEventListener(
        "wishlistUpdated",
        handleWishlistUpdate as EventListener,
      );
    };
  }, []);

  const handleWishlistUpdate = () => {
    fetchFavorites();
    // Trigger wishlist badge animation
    const wishlistBadge = document.querySelector(".wishlist-badge");
    if (wishlistBadge) {
      wishlistBadge.classList.remove("badge-jump");
      void (wishlistBadge as HTMLElement).offsetWidth; // Trigger reflow
      wishlistBadge.classList.add("badge-jump");
    }
  };

  const fetchFavCount = async () => {
    const res = await BaseApi.get("/favorites/count");
    if (res?.data) setFavCount(res.data);
  };

  const closeConfirmModal = () => {
    setConfirmConfig((c) => ({ ...c, isOpen: false }));
  };

  const handleRemoveAll = async () => {
    try {
      setRemovingAll(true);

      // Wait for animation to start
      // await new Promise(resolve => setTimeout(resolve, 100));

      const res = await BaseApi.delete("/favorites/clear");
      if (res.success) {
        setFavorites([]);
        setFavCount(0);
        // Dispatch wishlist update event
        window.dispatchEvent(
          new CustomEvent("wishlistUpdated", {
            detail: { action: "clearAll" },
          }),
        );

        await fetchFavorites();
      }
    } catch (error) {
      console.error("Failed to clear favorites:", error);
    } finally {
      setRemovingAll(false);
    }
  };
  const removeAllFavoritesConfirmation = () => {
    setConfirmConfig((c) => ({
      ...c,
      isOpen: true,
      title: t("Clear all favorites"),
      message: t("This will permanently remove all favorites."),
      confirmText: t("Delete"),
      cancelText: t("Cancel"),
      icon: Trash2,
      iconColor: "text-red-600",
      iconBg: "bg-red-100",
      showInput: false,
      onConfirm: async () => {
        closeConfirmModal();
        await handleRemoveAll();
      },
    }));
  };

  const shareAllFavoritesConfirmation = () => {
    setConfirmConfig((c) => ({
      ...c,
      isOpen: true,
      title: t("Share all favorites"),
      message: t("Please enter phone number to share your favorites"),
      inputPlaceholder: t("Enter phone number"),
      inputLabel: t("Phone Number"),
      confirmText: t("Share"),
      cancelText: t("Cancel"),
      requiredInput: true,
      showInput: true,
      icon: Share2,
      iconColor: "text-green-600",
      iconBg: "bg-green-100",
      onConfirm: async (phoneNumber?: string) => {
        if (!phoneNumber) return;
        closeConfirmModal();
        await shareFavoritesAll(phoneNumber);
      },
    }));
  };
  const shareFavoritesAll = async (phoneNumber: string) => {
    if (!phoneNumber.trim()) return;

    try {
      // Wait for animation to start
      // await new Promise(resolve => setTimeout(resolve, 100));
      const res = await BaseApi.post("/favorites/share", {
        recipientPhone: phoneNumber,
      });
      if (res.success && res.data) {
        success(
          t(res?.message) || "Shared favorites successfully to:" + phoneNumber,
        );
        // console.log("Shared favorites successfully to :", phoneNumber);
      }
    } catch (error) {
      console.error(t("Failed to share favorites with:") + phoneNumber, error);
    } finally {
    }
  };

  // const handleRemoveFavorite = async (id: string) => {
  //   await BaseApi.delete(`/favorites/${id}`);
  //   setFavorites(favorites.filter((f) => f.id !== id));
  //   setFavCount(favCount - 1);
  // };

  // const handleToggleFavorite = async (productId: number) => {
  //   await BaseApi.post("favorites/toggle", { productId });
  //   fetchFavorites();
  // };

  // const filteredFavorites = favorites.filter((f) =>
  //   f.product.name.en.toLowerCase().includes(searchQuery.toLowerCase()),
  // );

  if (loading) {
    // Icon={Heart}
    return <LoadingComponent title={t('Favorites')} />;
  }

  return (
    <div className="favorites-page">
      {/* Header */}
      <div className="favorites-header">
        <div className="favorites-header-content">
          <div className="favorites-title flex justify-between items-center w-full">
            {/* Icon */}
            <div className="favorites-icon">
              <Heart className="icon-heart" />
            </div>

            {/* Title & Count */}
            <div className="text-center">
              <h1 className="text-lg font-semibold">{t("My Favorites")}</h1>
              <p className="text-sm text-gray-500">
                {t("Items In WishList", { length: favorites.length || 0 })}
              </p>
            </div>

            {/* Share / Delete -  All Button */}
            <div>
              <button
                onClick={shareAllFavoritesConfirmation}
                disabled={removingAll || favorites.length === 0}
                className={`m-2 p-2 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  removingAll ? "removing animate-pulse" : ""
                }`}
                title="Share Favorites"
              >
                <Share2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              </button>

              {/* Delete All Button */}
              <button
                onClick={removeAllFavoritesConfirmation}
                disabled={removingAll || favorites.length === 0}
                className={`m-2 p-2 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  removingAll ? "removing animate-pulse" : ""
                }`}
                title="Delete All"
              >
                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      {/* <div className="flex w-full gap-4 px-12"> */}
      {/* Search Input */}
      {/* <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search favorites..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-500 focus:outline-none transition-colors"
          />
        </div> */}

      {/* Sort Dropdown */}
      {/* <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-6 py-3 border-2 border-gray-200 rounded-xl rounded-xl focus:border-pink-500 focus:outline-none appearance-none bg-white cursor-pointer whitespace-nowrap"
        >
          <option value="recent">Most Recent</option>
          <option value="price-low">Price: Low to High</option>
          <option value="price-high">Price: High to Low</option>
          <option value="name">Name A-Z</option>
        </select> */}
      {/* </div> */}

      {/* Empty State */}
      {!favorites.length && !loading ? (
        <div className="favorites-empty">
          <Heart className="empty-icon" />
          <h3>{t("No favorites yet")}</h3>
          <p>{t("Start adding products to your wishlist to see them here")}</p>
          <button className="btn-primary" onClick={() => navigate("/products")}>
            {t("Browse Products")}
          </button>
        </div>
      ) : (
        <div className="w-full gap-4 px-12">
          {/*  Carousel */}
          <ProductsCarousel
            products={favorites.filter((f) => f?.product).map((f) => f.product)}
            renderHeader={false}
          />
        </div>
      )}
      {/* // Add Pagination before closing div: */}
      {favorites.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={setPage}
          isLoading={loading}
        />
      )}

      <ConfirmModal
        isOpen={confirmConfig.isOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText}
        showInput={confirmConfig.showInput}
        inputLabel={confirmConfig.inputLabel}
        inputPlaceholder={confirmConfig.inputPlaceholder}
        requiredInput={confirmConfig.requiredInput}
        icon={confirmConfig.icon}
        iconColor={confirmConfig.iconColor}
        iconBg={confirmConfig.iconBg}
        onConfirm={confirmConfig.onConfirm}
        onCancel={closeConfirmModal}
      />
    </div>
  );
};

export default FavoritesManagement;
